import type { SourceHealth } from '@/api/types/entities';
import type { StatusVariant } from '@/components/ui/StatusBadge';

export type { SourceHealth };

export const INFO_CODE_NO_NEW_LINKS = 'NO_NEW_LINKS';
export const PREVIOUS_ERROR_STALE_HOURS = 24;

function truncate(value: string | null | undefined, max = 200): string | null {
  if (!value) return null;
  const oneLine = value.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= max) return oneLine;
  return `${oneLine.slice(0, max - 1)}…`;
}

export function sourceHealthVariant(health: SourceHealth): StatusVariant {
  switch (health.status) {
    case 'OK':
      return 'success';
    case 'RECOVERING':
    case 'DEGRADED':
      return 'warning';
    case 'COOLDOWN':
      if (health.root_error_code === 'ACCESS_DENIED') return 'danger';
      return 'warning';
    case 'BLOCKED':
    case 'FAILING':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function sourceHealthLabel(health: SourceHealth): string {
  if (health.status === 'BLOCKED') {
    if (health.current_error_code === 'ACCESS_DENIED') return 'BLOCKED: Access denied';
    if (health.current_error_code === 'CAPTCHA') return 'BLOCKED: ShieldSquare Captcha';
    return 'BLOCKED';
  }
  if (health.status === 'COOLDOWN') {
    if (health.root_error_code) return `COOLDOWN (${health.root_error_code})`;
    return 'COOLDOWN';
  }
  if (health.status === 'FAILING' && health.current_error_code === 'DATA_INTEGRITY_ERROR') {
    return 'FAILING: Data integrity';
  }
  if (health.status === 'DEGRADED' && health.current_error_code === 'PARSER_ZERO_RESULTS') {
    return 'DEGRADED: No links extracted';
  }
  if (health.status === 'RECOVERING') return 'RECOVERING';
  return health.status;
}

export function sourceHealthPreviousLabel(health: SourceHealth): string | null {
  if (!health.previous_error_code || !health.previous_error_message) return null;
  const short = truncate(health.previous_error_message.split('\n')[0], 120);
  const age =
    health.previous_error_age_hours != null
      ? ` (${Math.round(health.previous_error_age_hours)}h ago)`
      : '';
  const stale =
    health.previous_error_age_hours != null &&
    health.previous_error_age_hours >= PREVIOUS_ERROR_STALE_HOURS
      ? ' · stale'
      : '';
  return `Previous error${age}${stale}: ${health.previous_error_code} — ${short ?? ''}`;
}

export function sourceHealthTitle(health: SourceHealth): string {
  const parts = [
    health.current_error_code ? `Current: ${health.current_error_code}` : 'Current: OK',
    truncate(health.current_error_message),
    health.info_code ? `Info: ${health.info_code}` : null,
    truncate(health.info_message),
    health.previous_error_code ? `Previous: ${health.previous_error_code}` : null,
    truncate(health.previous_error_message),
    health.previous_error_age_hours != null
      ? `Previous age: ${Math.round(health.previous_error_age_hours)}h`
      : null,
    health.root_error_code ? `Root: ${health.root_error_code}` : null,
    truncate(health.root_error_message),
    health.cooldown_until ? `Cooldown until: ${health.cooldown_until}` : null,
    health.success_rate_24h != null
      ? `Success rate (24h): ${Math.round(health.success_rate_24h * 100)}%`
      : null,
    `Links (24h): ${health.links_24h}`,
    `Failures (24h): ${health.failures_24h}`,
    `Zero-result runs (24h): ${health.zero_result_runs_24h}`,
    `Consecutive zero-result runs: ${health.consecutive_zero_result_runs}`,
  ].filter(Boolean);
  return parts.join('\n');
}

export function sourceHealthRecommendedAction(health: SourceHealth): string | null {
  if (health.recommended_action) return health.recommended_action;
  if (health.source_name === 'mobile_de' && (health.status === 'BLOCKED' || health.status === 'COOLDOWN')) {
    return (
      'Blocked by mobile.de bot protection. Recommended: wait for cooldown, use lower frequency, ' +
      'configure persistent browser profile, or configure proxy.'
    );
  }
  return null;
}

export function formatSuccessRate(rate: number | null | undefined): string {
  if (rate == null) return '—';
  return `${Math.round(rate * 100)}%`;
}

export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function sourceHealthDetailHint(health: SourceHealth): string | null {
  if (health.current_error_code === 'DATA_INTEGRITY_ERROR') {
    return 'Data integrity issue — stale job or missing subscription. Check queue.';
  }
  if (health.status === 'COOLDOWN') {
    const parts: string[] = [];
    if (health.root_error_message) {
      parts.push(truncate(health.root_error_message, 160) ?? health.root_error_message);
    }
    if (health.cooldown_until) {
      parts.push(`Cooldown until ${health.cooldown_until}`);
    }
    return parts.length ? parts.join(' · ') : null;
  }
  if (health.current_error_code === 'PARSER_ZERO_RESULTS') {
    return (
      truncate(health.current_error_message, 160) ??
      'Repeated zero-result scrapes with no links in the last 24 hours'
    );
  }
  if (health.info_code === INFO_CODE_NO_NEW_LINKS && health.info_message) {
    return health.info_message;
  }
  return sourceHealthPreviousLabel(health);
}
